import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { isObject, merge } from 'lodash'

import type { SortOrder } from '~/components/Table/hooks/useTableDataFetching'

type ServerErrorData = {
  error?: string
  message: string
  path?: string
  status?: number
  timestamp?: number
  code?: string
  /** Spring-style field validation map (e.g. billing API) */
  errors?: Record<string, string | string[]>
}

type ServerError = {
  status: number
  data: ServerErrorData
}

export type RtkQueryError = FetchBaseQueryError | SerializedError | ServerError
type ErrorMessageMapNoDefault = Record<number, string>
type ErrorMessageMapWithDefault = ErrorMessageMapNoDefault & { default: string }
export type ErrorMessageMap = ErrorMessageMapNoDefault | ErrorMessageMapWithDefault

const defaultErrorMessageMap: ErrorMessageMap = {
  400: 'Invalid parameters.',
  default: 'Something went wrong.'
}

export function createErrorMessageMap(overrides: ErrorMessageMap): ErrorMessageMap {
  return merge({}, defaultErrorMessageMap, overrides)
}

// TODO: add in support for an optional message param that will be preferentially displayed
export function parseError(error: unknown, messageMap?: ErrorMessageMapWithDefault): string
export function parseError(error: unknown, messageMap?: ErrorMessageMapNoDefault): string | undefined
export function parseError(error: unknown, messageMap = defaultErrorMessageMap) {
  const defaultMessage = 'default' in messageMap ? messageMap.default : undefined

  if (!isObject(error)) {
    return defaultMessage
  }

  // If it's a JsonApiErrorResponse, prefer the caller's status-keyed entry
  // (for i18n) and fall back to the server's English `detail` only when the
  // map has no entry for this status. Callers that only pass `default` keep
  // the original behaviour: detail wins over default.
  if ('data' in error && isJsonApiErrorResponse(error.data)) {
    if ('status' in error && typeof error.status === 'number') {
      const mapped = messageMap[error.status]
      if (mapped) return mapped
    }
    if (error.data.errors.length) {
      return error.data.errors[0]!.detail
    }
  }

  // It's a `SerializedError`, not a server error
  if (!('status' in error)) return defaultMessage

  // If it returns a user readable error, use that (and append field-level validation when present)
  if (isServerError(error)) return formatServerErrorMessage(error.data)

  const code = error.status
  if (typeof code !== 'number') {
    // One of `FETCH_ERROR`, `PARSING_ERROR`, `TIMEOUT_ERROR`, or `CUSTOM_ERROR`-- see the `FetchBaseQueryError` type
    return defaultMessage
  } else {
    return messageMap[code] ?? defaultMessage
  }
}

export function parseSortString(sortOrder?: SortOrder | null, sortBy?: string | unknown) {
  if (!sortOrder || !sortBy) return
  const sortPrefix = sortOrder === 'asc' ? '' : '-'
  return `${sortPrefix}${sortBy}`
}

function isServerError(error: object): error is ServerError {
  if (!('data' in error)) return false

  return error.data && typeof error.data === 'object' ? 'message' in error.data : false
}

function formatServerErrorMessage(data: ServerErrorData): string {
  const base = data.message
  const raw = data.errors
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return base
  }
  const parts = Object.entries(raw).map(([k, v]) =>
    Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${String(v)}`
  )
  if (!parts.length) return base
  return `${base} — ${parts.join('; ')}`
}

type JsonApiErrorResponse = {
  errors: Array<{
    id: string
    title: string
    status: string
    source?: {
      parameter: string
    }
    detail: string
    code: string
  }>
}

/**
 * Which request parameter a rejection blames, when it names one.
 *
 * JSON:API carries `source.parameter` alongside the explanation, and a form
 * that knows the parameter can put the message on the field that caused it
 * rather than in a banner the reader has to match up themselves. Optional in
 * the format and absent from most errors, so callers need a fallback.
 */
export function parseErrorSourceParameter(error: unknown): string | undefined {
  if (!isObject(error) || !('data' in error) || !isJsonApiErrorResponse(error.data)) return undefined
  const parameter = error.data.errors[0]?.source?.parameter
  return typeof parameter === 'string' && parameter !== '' ? parameter : undefined
}

/**
 * The server's own explanation for a rejection, when the response is JSON:API shaped.
 *
 * Useful when `source.parameter` doesn't name the field cleanly enough to act on — a
 * Pydantic validator on an `Optional[...]` field reports its `loc` ending in the union
 * branch it matched (e.g. `str`) rather than the field name, so `source.parameter` for
 * that error is that branch tag, not the field. The `detail` text is still the
 * validator's own message, so callers can match on it instead.
 */
export function parseErrorDetail(error: unknown): string | undefined {
  if (!isObject(error) || !('data' in error) || !isJsonApiErrorResponse(error.data)) return undefined
  const detail = error.data.errors[0]?.detail
  return typeof detail === 'string' && detail !== '' ? detail : undefined
}

/**
 * The machine-readable code on a JSON:API rejection, when it carries one.
 *
 * A status says only that a request was refused; the code says which refusal
 * it was, so a caller can give a specific refusal its own message while every
 * other error of that status keeps the generic one.
 */
export function parseErrorCode(error: unknown): string | undefined {
  if (!isObject(error) || !('data' in error) || !isJsonApiErrorResponse(error.data)) return undefined
  const code: unknown = error.data.errors[0]?.code
  return typeof code === 'string' && code !== '' ? code : undefined
}

export function isJsonApiErrorResponse(obj: unknown): obj is JsonApiErrorResponse {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }

  if (!('errors' in obj) || !Array.isArray(obj.errors)) {
    return false
  }

  return obj.errors.every(
    (error) => typeof error?.title === 'string' && typeof error.status === 'string' && typeof error.detail === 'string'
  )
}
